import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { Badge, Body2, Body3, Caption, Card, Subtitle1, Subtitle2, XStack, YStack } from "@medbillai/ui";
import { dollars } from "./caseCard";
import { KIND_LABEL, type RecordItem, SHORT_LABEL } from "./records";
dayjs.extend(utc);
const figure = (record: RecordItem): string => record.amount === undefined ? record.paid ? "Paid" : "—" : dollars(record.amount);
const amountLabel = (record: RecordItem): string | null => record.paid ? record.amount === undefined ? null : "Paid" : SHORT_LABEL[record.kind];

// Service dates are UTC calendar dates.
const DateColumn = ({
  date
}: {
  date?: Date;
}) => <YStack width="$si4" alignItems="flex-start" flexShrink={0} data-sentry-element="YStack" data-sentry-component="DateColumn" data-sentry-source-file="RecordRow.tsx">
    {date ? <>
        <Caption color="$textSubtle">{dayjs.utc(date).format("MMM")}</Caption>
        <Subtitle1>{dayjs.utc(date).format("DD")}</Subtitle1>
      </> : <Subtitle1 color="$textSubtle">—</Subtitle1>}
  </YStack>;

// An outline, like the DS focus ring, so selecting a row never moves its content.
const SELECTED_RING = {
  outlineWidth: "$border2",
  outlineColor: "$borderBrandStrong",
  outlineStyle: "solid",
  outlineOffset: 0
} as const;
export type RecordRowProps = {
  record: RecordItem;
  selected?: boolean;
  onPress: () => void;
};
export const RecordRow = ({
  record,
  selected,
  onPress
}: RecordRowProps) => {
  const detail = [record.patient, record.detailLine].filter(Boolean).join(" · ");
  const label = amountLabel(record);
  return <Card variant="raised" size="medium" onPress={onPress} testID={`bills-claims.row-${record.key}`} flexDirection="row" alignItems="center" gap="$sp1.5" paddingVertical="$sp1.5" paddingHorizontal="$sp2" {...selected ? SELECTED_RING : {}} data-sentry-element="Card" data-sentry-component="RecordRow" data-sentry-source-file="RecordRow.tsx">
      <DateColumn date={record.serviceDate} data-sentry-element="DateColumn" data-sentry-source-file="RecordRow.tsx" />
      <YStack flex={1} minWidth={0} gap="$sp0.25" data-sentry-element="YStack" data-sentry-source-file="RecordRow.tsx">
        <XStack minWidth={0} data-sentry-element="XStack" data-sentry-source-file="RecordRow.tsx">
          <Subtitle2 numberOfLines={1} flexShrink={1} data-sentry-element="Subtitle2" data-sentry-source-file="RecordRow.tsx">
            {record.provider ?? KIND_LABEL[record.kind]}
          </Subtitle2>
        </XStack>
        {detail ? <Body2 color="$textSubtle" numberOfLines={1}>
            {detail}
          </Body2> : null}
      </YStack>
      <YStack alignItems="flex-end" gap="$sp0.5" flexShrink={0} data-sentry-element="YStack" data-sentry-source-file="RecordRow.tsx">
        {record.badge ? <>
            <Badge size="small" bordered={false} status={record.badge.tone}>
              {record.badge.label}
            </Badge>
            <Body2>{figure(record)}</Body2>
          </> : <>
            <Body2>{figure(record)}</Body2>
            {label ? <Body3 color="$textSubtle">{label}</Body3> : null}
          </>}
      </YStack>
    </Card>;
};